/**
 * Chat Session List Component
 *
 * Modern session list with time-based grouping and enhanced visual design
 *
 * Features:
 * - Time-based grouping (Today, Yesterday, This Week, etc.)
 * - Relative timestamps with absolute time tooltips
 * - Message count indicators
 * - Smooth animations and transitions
 * - Archive/Active tabs
 * - Context menu for session actions (archive, rename, delete)
 */

import {
  Archive,
  ArchiveRestore,
  MessageSquare,
  Pencil,
  Pin,
  PinOff,
  SearchX,
  Trash2,
} from 'lucide-react';
import { usePinnedSessionsStore } from '../stores/pinned-sessions-store';
import React from 'react';
import type { ArchiveFilter, LocalChatSession } from '../stores/s3-chat-store';
import { useS3ChatStore } from '../stores/s3-chat-store';
import { cn } from '../utils/cn';
import { getSearchStats, type MessageSearchResult } from '../utils/message-search';
import { formatAbsoluteTime, formatRelativeTime, getTimePeriod } from '../utils/time-format';
import { ChatThreadItem } from './chat-layout';
import { ChatSessionListSkeleton } from './chat-session-list-skeleton';
import { Tooltip } from './tooltip';

export type ChatSessionAction = 'archive' | 'unarchive' | 'rename' | 'delete' | 'pin' | 'unpin';

type ChatSessionListProps = {
  sessions: LocalChatSession[]; // Filtered sessions for display (based on current tab)
  allSessions: LocalChatSession[]; // All sessions for search (both active & archived)
  currentSessionId: string | null;
  isLoading?: boolean;
  onSelectSession: (sessionId: string, messageId?: string) => void;
  groupByTime?: boolean;
  // Archive-related props
  archiveFilter: ArchiveFilter;
  onArchiveFilterChange: (filter: ArchiveFilter) => void;
  archivedCount: number;
  // Action callbacks
  onArchive: (sessionId: string) => void;
  onUnarchive: (sessionId: string) => void;
  onRename: (sessionId: string, currentTitle: string) => void;
  /** Optional — performs an inline rename without opening the modal. */
  onInlineRename?: (sessionId: string, newTitle: string) => Promise<void>;
  onDelete: (sessionId: string, sessionTitle: string) => void;
  // Search props - managed by parent (ChatView)
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  searchResults: MessageSearchResult[];
  searchProgress: {
    isDeepSearching: boolean;
    loadedCount: number;
    totalCount: number;
    currentSession: string | null;
  };
};

type GroupedSessions = {
  period: string;
  sessions: LocalChatSession[];
};

// Escape regex special characters to prevent regex injection and XSS
const escapeRegExp = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

export const ChatSessionList: React.FC<ChatSessionListProps> = ({
  sessions,
  allSessions,
  currentSessionId,
  isLoading = false,
  onSelectSession,
  groupByTime = true,
  archiveFilter,
  onArchiveFilterChange,
  archivedCount,
  onArchive,
  onUnarchive,
  onRename,
  onInlineRename,
  onDelete,
  searchQuery,
  onSearchQueryChange,
  searchResults,
  searchProgress,
}) => {
  // Pinned sessions — localStorage-backed, survives refreshes
  const pinnedMap = usePinnedSessionsStore(state => state.pinned);
  const pinSession = usePinnedSessionsStore(state => state.pin);
  const unpinSession = usePinnedSessionsStore(state => state.unpin);

  // Initial-mount loader — true only on the very first fetch before any
  // sessions have arrived. Subsequent refreshes keep the list visible.
  const isLoadingSessions = useS3ChatStore(state => state.isLoadingSessions);
  const showInitialSkeleton = isLoadingSessions && allSessions.length === 0 && !searchQuery.trim();

  // State for collapsed groups — persisted across sessions so users don't have
  // to re-collapse "Older" every time they open the app.
  const COLLAPSED_GROUPS_KEY = 'chat-session-list:collapsed-groups';
  const [collapsedGroups, setCollapsedGroups] = React.useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = window.localStorage.getItem(COLLAPSED_GROUPS_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? new Set(parsed) : new Set();
    } catch {
      return new Set();
    }
  });

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        COLLAPSED_GROUPS_KEY,
        JSON.stringify(Array.from(collapsedGroups))
      );
    } catch {
      // localStorage disabled — silently no-op
    }
  }, [collapsedGroups]);

  // Inline rename state (double-click a title)
  const [editingSessionId, setEditingSessionId] = React.useState<string | null>(null);
  const [editingTitle, setEditingTitle] = React.useState('');
  const [isCommittingRename, setIsCommittingRename] = React.useState(false);

  const startInlineRename = React.useCallback((sessionId: string, currentTitle: string) => {
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle);
  }, []);

  const cancelInlineRename = React.useCallback(() => {
    setEditingSessionId(null);
    setEditingTitle('');
  }, []);

  const commitInlineRename = React.useCallback(
    async (sessionId: string, originalTitle: string) => {
      const next = editingTitle.trim();
      if (!next || next === originalTitle.trim() || !onInlineRename) {
        cancelInlineRename();
        return;
      }
      setIsCommittingRename(true);
      try {
        await onInlineRename(sessionId, next);
      } catch {
        // Toast shown upstream
      } finally {
        setIsCommittingRename(false);
        cancelInlineRename();
      }
    },
    [editingTitle, onInlineRename, cancelInlineRename]
  );

  // Toggle group collapse
  const toggleGroup = (period: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(period)) {
        next.delete(period);
      } else {
        next.add(period);
      }
      return next;
    });
  };

  // Get search stats - use allSessions to show total searchable count
  const searchStats = React.useMemo(() => getSearchStats(allSessions), [allSessions]);

  // Filter search results by current tab (active/archived)
  const filteredSearchResults = React.useMemo(() => {
    if (searchResults.length === 0) return [];

    // Get set of session IDs that match current tab filter
    const currentTabSessionIds = new Set(sessions.map(s => s.id));

    // Filter search results to only include matches from current tab
    return searchResults.filter(result => currentTabSessionIds.has(result.sessionId));
  }, [searchResults, sessions]);

  // Filter sessions by title (for backward compatibility and quick filter)
  const filteredSessions = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return sessions;
    }

    // If we have message search results, show sessions that contain matches
    if (filteredSearchResults.length > 0) {
      const sessionIdsWithMatches = new Set(filteredSearchResults.map(r => r.sessionId));
      return sessions.filter(session => sessionIdsWithMatches.has(session.id));
    }

    // Fallback: simple title search
    const query = searchQuery.toLowerCase();
    return sessions.filter(session => session.title.toLowerCase().includes(query));
  }, [sessions, searchQuery, filteredSearchResults]);

  // Group sessions by time period if enabled.
  // Pinned sessions are pulled out into their own group at the top and excluded
  // from their time buckets so they don't appear twice.
  const groupedSessions: GroupedSessions[] = React.useMemo(() => {
    // Collect pinned sessions that exist in the current filtered set, sorted by
    // when they were pinned (most recently pinned at the top).
    const pinnedSessions = filteredSessions
      .filter(s => pinnedMap[s.id])
      .sort((a, b) => (pinnedMap[b.id] ?? 0) - (pinnedMap[a.id] ?? 0));
    const pinnedIds = new Set(pinnedSessions.map(s => s.id));
    const unpinnedSessions = filteredSessions.filter(s => !pinnedIds.has(s.id));

    if (!groupByTime) {
      const result: GroupedSessions[] = [];
      if (pinnedSessions.length > 0) {
        result.push({ period: 'Pinned', sessions: pinnedSessions });
      }
      result.push({ period: 'All Chats', sessions: unpinnedSessions });
      return result;
    }

    const groups = new Map<string, LocalChatSession[]>();

    unpinnedSessions.forEach(session => {
      const period = getTimePeriod(session.updatedAt);
      const existing = groups.get(period) || [];
      groups.set(period, [...existing, session]);
    });

    // Convert to array and sort by period priority
    const periodOrder = [
      'Today',
      'Yesterday',
      'This Week',
      'Last Week',
      'This Month',
      'Last Month',
      'Older',
    ];

    const result: GroupedSessions[] = [];
    if (pinnedSessions.length > 0) {
      result.push({ period: 'Pinned', sessions: pinnedSessions });
    }
    periodOrder
      .filter(period => groups.has(period))
      .forEach(period => result.push({ period, sessions: groups.get(period) || [] }));
    return result;
  }, [filteredSessions, groupByTime, pinnedMap]);

  // Handle context menu action
  const handleAction = React.useCallback(
    (action: ChatSessionAction, sessionId: string) => {
      const session = sessions.find(s => s.id === sessionId);
      if (!session) return;

      switch (action) {
        case 'archive':
          onArchive(sessionId);
          break;
        case 'unarchive':
          onUnarchive(sessionId);
          break;
        case 'rename':
          onRename(sessionId, session.title);
          break;
        case 'delete':
          onDelete(sessionId, session.title);
          break;
        case 'pin':
          pinSession(sessionId);
          break;
        case 'unpin':
          unpinSession(sessionId);
          break;
      }
    },
    [sessions, onArchive, onUnarchive, onRename, onDelete, pinSession, unpinSession]
  );

  return (
    <div className="flex flex-col h-full overflow-x-hidden">
      {/* Search bar */}
      <div className="px-3 pb-2 pt-1 flex-shrink-0">
        <div className="relative group">
          <input
            type="text"
            placeholder={
              archiveFilter === 'archived' ? 'Search archived...' : 'Search conversations...'
            }
            value={searchQuery}
            onChange={e => onSearchQueryChange(e.target.value)}
            className="w-full px-3 py-1.5 pl-8 text-sm rounded-md
 bg-muted/40 border border-border/40
 focus:border-primary/50 focus:bg-background focus:outline-none
 transition-all duration-200 placeholder:text-muted-foreground/50"
          />
          {/* Search icon or spinner */}
          {searchProgress.isDeepSearching ? (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          ) : (
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/50
 group-hover:text-muted-foreground/70 transition-colors duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          )}
          {/* Clear button */}
          {searchQuery && (
            <button
              onClick={() => onSearchQueryChange('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full
 hover:bg-muted transition-colors"
              aria-label="Clear search"
            >
              <svg
                className="w-3.5 h-3.5 text-muted-foreground/60 group-hover/clear:text-muted-foreground transition-colors"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Search stats indicator */}
        {searchQuery.trim().length >= 2 && (
          <div className="px-2 py-1.5 space-y-1.5">
            {/* Progress bar for deep search */}
            {searchProgress.isDeepSearching && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-[10px]">
                  <div className="flex items-center gap-1.5 text-primary">
                    <div className="w-3 h-3 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                    <span className="font-medium">Deep searching...</span>
                  </div>
                  <span className="text-muted-foreground/60">
                    {searchProgress.loadedCount}/{searchProgress.totalCount}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="h-1 bg-muted/30 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-300 ease-out"
                    style={{
                      width: `${(searchProgress.loadedCount / searchProgress.totalCount) * 100}%`,
                    }}
                  />
                </div>

                {/* Currently loading session */}
                {searchProgress.currentSession && (
                  <div className="text-[9px] text-muted-foreground/60 truncate">
                    Loading: {searchProgress.currentSession}
                  </div>
                )}
              </div>
            )}

            {/* Stats line */}
            <div className="text-[10px] text-muted-foreground/60 flex items-center gap-1.5">
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 16 16">
                <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
              </svg>
              <span>
                {searchProgress.isDeepSearching
                  ? `Found ${filteredSearchResults.length} matches so far`
                  : `${filteredSearchResults.length} ${filteredSearchResults.length === 1 ? 'match' : 'matches'} • ${searchStats.loadedSessions}/${searchStats.totalSessions} conversations`}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sessions list */}
      {showInitialSkeleton ? (
        <div className="flex-1 overflow-hidden pt-3">
          <ChatSessionListSkeleton />
        </div>
      ) : sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          {archiveFilter === 'archived' ? (
            <>
              <Archive className="w-10 h-10 text-muted-foreground/30 mb-3" />
              <div className="text-muted-foreground text-sm mb-2">No archived chats</div>
              <div className="text-muted-foreground/60 text-xs">
                Archive chats to keep them out of your main list
              </div>
            </>
          ) : (
            <>
              <MessageSquare className="w-10 h-10 text-muted-foreground/30 mb-3" />
              <div className="text-muted-foreground text-sm mb-2">No chat threads yet</div>
              <div className="text-muted-foreground/60 text-xs">
                Start a new conversation to begin
              </div>
            </>
          )}
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center animate-in fade-in duration-300">
          {searchProgress.isDeepSearching ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1.5 h-1.5 bg-primary rounded-full typing-dot" />
                <div
                  className="w-1.5 h-1.5 bg-primary rounded-full typing-dot"
                  style={{ animationDelay: '0.15s' }}
                />
                <div
                  className="w-1.5 h-1.5 bg-primary rounded-full typing-dot"
                  style={{ animationDelay: '0.3s' }}
                />
              </div>
              <div className="text-muted-foreground text-sm mb-1">
                Searching all conversations...
              </div>
              <div className="text-xs text-muted-foreground/60">
                {filteredSearchResults.length > 0
                  ? `Found ${filteredSearchResults.length} ${filteredSearchResults.length === 1 ? 'match' : 'matches'} so far`
                  : 'No matches yet, still searching...'}
              </div>
            </>
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-muted/40 flex items-center justify-center mb-4">
                <SearchX className="w-7 h-7 text-muted-foreground/60" />
              </div>
              <div className="text-sm font-medium text-foreground mb-1.5">No matches found</div>
              <div className="text-xs text-muted-foreground/80 max-w-[240px] leading-relaxed mb-4">
                No chats contain{' '}
                <span className="font-semibold text-foreground">
                  &ldquo;{searchQuery.length > 30 ? `${searchQuery.slice(0, 30)}…` : searchQuery}
                  &rdquo;
                </span>
                . Try a shorter query or different keywords.
              </div>
              <button
                type="button"
                onClick={() => onSearchQueryChange('')}
                className="text-xs font-medium text-primary hover:underline transition-colors"
              >
                Clear search
              </button>
            </>
          )}
        </div>
      ) : filteredSearchResults.length > 0 ? (
        /* Message search results view */
        <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-2 px-2 custom-scrollbar">
          <div className="px-2 py-2 flex items-center justify-between sticky top-0 bg-background z-10">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Found in Messages
            </div>
            <div className="text-xs text-muted-foreground/60">
              {filteredSearchResults.length}{' '}
              {filteredSearchResults.length === 1 ? 'match' : 'matches'}
            </div>
          </div>

          {filteredSearchResults.slice(0, 50).map(result => (
            <button
              key={`${result.sessionId}-${result.messageId}`}
              onClick={() => onSelectSession(result.sessionId, result.messageId)}
              aria-label={`${result.sessionTitle} - ${result.messageSender === 'user' ? 'You' : 'AI'}: ${result.snippet.replace(/<[^>]*>/g, '')}`}
              className="w-full text-left px-3 py-2.5 rounded-xl
 glass-surface-bg glass-surface-border hover:glass-surface-hover
 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              {/* Session title header */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-xs font-medium text-foreground/90 truncate flex-1">
                  {result.sessionTitle}
                </div>
                <div
                  className="text-[10px] text-muted-foreground/60 ml-2"
                  title={formatAbsoluteTime(result.messageTimestamp)}
                >
                  {formatRelativeTime(result.messageTimestamp)}
                </div>
              </div>

              {/* Sender badge */}
              <div className="flex items-center gap-1.5 mb-1.5">
                <div
                  className={`
 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium
 ${
   result.messageSender === 'user'
     ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
     : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
 }
 `}
                >
                  {result.messageSender === 'user' ? (
                    <>
                      <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                      </svg>
                      You
                    </>
                  ) : (
                    <>
                      <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
                        <path d="M2.165 15.803l.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a10.97 10.97 0 0 0 .398-2z" />
                      </svg>
                      AI
                    </>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground/40">•</div>
                <div className="text-[10px] text-muted-foreground/60">
                  Match: {Math.round((1 - result.matchScore) * 100)}%
                </div>
              </div>

              {/* Message snippet */}
              <div
                className="text-xs text-muted-foreground leading-relaxed line-clamp-3"
                dangerouslySetInnerHTML={{
                  __html: result.snippet.replace(
                    new RegExp(`(${escapeRegExp(searchQuery)})`, 'gi'),
                    '<mark class="bg-primary/20 text-foreground rounded px-0.5">$1</mark>'
                  ),
                }}
              />
            </button>
          ))}

          {filteredSearchResults.length > 50 && (
            <div className="px-3 py-2 text-center text-xs text-muted-foreground/60">
              Showing first 50 of {filteredSearchResults.length} matches
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scrollbar pt-3 pb-1">
          {groupedSessions.map(group => (
            <div key={group.period}>
              {/* Group header — sticky so the current bucket stays visible while scrolling */}
              {groupByTime && (
                <button
                  onClick={() => toggleGroup(group.period)}
                  className={cn(
                    'w-full flex items-center justify-between px-4 py-1.5',
                    'sticky top-0 z-10',
                    'bg-background/80 backdrop-blur-sm',
                    'transition-colors duration-150 group hover:bg-muted/30'
                  )}
                >
                  <span
                    className={cn(
                      'text-[11px] font-semibold uppercase tracking-widest flex items-center gap-1.5',
                      group.period === 'Pinned' || group.period === 'Today'
                        ? 'text-primary/80'
                        : 'text-muted-foreground/60'
                    )}
                  >
                    {group.period === 'Pinned' && (
                      <Pin className="w-3 h-3 rotate-45" aria-hidden="true" />
                    )}
                    {group.period}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'inline-flex items-center justify-center',
                        'min-w-[18px] h-[18px] px-1.5 rounded-full',
                        'text-[10px] font-medium tabular-nums',
                        group.period === 'Pinned' || group.period === 'Today'
                          ? 'bg-primary/15 text-primary/80'
                          : 'bg-muted/50 text-muted-foreground/60'
                      )}
                    >
                      {group.sessions.length}
                    </span>
                    <svg
                      className={cn(
                        'w-2.5 h-2.5 text-muted-foreground/40 transition-transform duration-200',
                        collapsedGroups.has(group.period) ? '-rotate-90' : 'rotate-0'
                      )}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>
              )}

              {/* Session rows */}
              {!collapsedGroups.has(group.period) && (
                <div className="px-2 py-2 space-y-0.5">
                  {group.sessions.map(session => (
                    <div key={session.id} className="relative group/row">
                      <ChatThreadItem
                        isActive={session.id === currentSessionId}
                        onClick={() => onSelectSession(session.id)}
                        className={cn(
                          'pr-2 py-2.5 transition-[padding-right] duration-150 group-hover/row:pr-[8rem]',
                          isLoading &&
                            session.id !== currentSessionId &&
                            'opacity-50 cursor-not-allowed'
                        )}
                        title={formatAbsoluteTime(session.updatedAt)}
                      >
                        <div className="flex flex-col gap-1 w-full">
                          <div className="flex items-center justify-between w-full gap-2">
                            {editingSessionId === session.id ? (
                              <input
                                autoFocus
                                value={editingTitle}
                                onChange={e => setEditingTitle(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                onDoubleClick={e => e.stopPropagation()}
                                onBlur={() => commitInlineRename(session.id, session.title)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    commitInlineRename(session.id, session.title);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelInlineRename();
                                  }
                                }}
                                maxLength={200}
                                disabled={isCommittingRename}
                                className={cn(
                                  'truncate text-[13px] font-medium leading-snug text-foreground',
                                  'w-full bg-transparent outline-none',
                                  'border-b border-primary/50 focus:border-primary',
                                  'px-0 py-0'
                                )}
                                aria-label="Rename chat"
                              />
                            ) : (
                              <div
                                className="flex items-center gap-1.5 min-w-0 flex-1"
                                onDoubleClick={e => {
                                  if (!onInlineRename) return;
                                  e.stopPropagation();
                                  startInlineRename(session.id, session.title);
                                }}
                                title={onInlineRename ? 'Double-click to rename' : undefined}
                              >
                                {pinnedMap[session.id] && (
                                  <Pin
                                    className="w-3 h-3 text-primary/70 shrink-0 rotate-45"
                                    aria-label="Pinned"
                                  />
                                )}
                                <span className="truncate text-[13px] font-medium leading-snug text-foreground select-none">
                                  {session.title}
                                </span>
                              </div>
                            )}
                            {isLoading && session.id === currentSessionId && (
                              <div className="w-1.5 h-1.5 bg-primary rounded-full animate-pulse flex-shrink-0" />
                            )}
                          </div>

                          <div className="flex items-center justify-between w-full gap-2">
                            <span className="text-[11px] text-muted-foreground/60">
                              {formatRelativeTime(session.updatedAt)}
                            </span>
                            {session.messageCount > 0 && (
                              <span className="text-[11px] text-muted-foreground/50 flex items-center gap-0.5">
                                <MessageSquare className="w-2.5 h-2.5" />
                                {session.messageCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </ChatThreadItem>

                      {/* Inline action icons — eraser strip, fades in on row hover */}
                      <div
                        className={cn(
                          'absolute right-0 top-0 bottom-0 w-[7.5rem]',
                          'flex items-center justify-center gap-1',
                          'bg-muted/60 rounded-r-lg',
                          'opacity-0 group-hover/row:opacity-100 transition-opacity duration-150'
                        )}
                      >
                        {(() => {
                          const isPinned = Boolean(pinnedMap[session.id]);
                          return (
                            <Tooltip
                              content={isPinned ? 'Unpin from top' : 'Pin to top'}
                              side="top"
                              delay={600}
                            >
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleAction(isPinned ? 'unpin' : 'pin', session.id);
                                }}
                                className={cn(
                                  'p-1 rounded transition-colors',
                                  isPinned
                                    ? 'text-primary hover:bg-primary/10'
                                    : 'text-muted-foreground/50 hover:text-foreground hover:bg-muted/80'
                                )}
                                aria-label={isPinned ? 'Unpin chat' : 'Pin chat'}
                              >
                                {isPinned ? (
                                  <PinOff className="w-3.5 h-3.5" />
                                ) : (
                                  <Pin className="w-3.5 h-3.5" />
                                )}
                              </button>
                            </Tooltip>
                          );
                        })()}

                        <Tooltip
                          content={session.isArchived ? 'Unarchive' : 'Archive'}
                          side="top"
                          delay={600}
                        >
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              handleAction(
                                session.isArchived ? 'unarchive' : 'archive',
                                session.id
                              );
                            }}
                            className="p-1 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted/80 transition-colors"
                            aria-label={session.isArchived ? 'Unarchive' : 'Archive'}
                          >
                            {session.isArchived ? (
                              <ArchiveRestore className="w-3.5 h-3.5" />
                            ) : (
                              <Archive className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </Tooltip>

                        <Tooltip content="Rename" side="top" delay={600}>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              handleAction('rename', session.id);
                            }}
                            className="p-1 rounded text-muted-foreground/50 hover:text-foreground hover:bg-muted/80 transition-colors"
                            aria-label="Rename"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </Tooltip>

                        <Tooltip content="Delete" side="top" delay={600}>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              handleAction('delete', session.id);
                            }}
                            className="p-1 rounded text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                            aria-label="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
